'use client'
import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/hooks/use-auth'

interface AuthGuardProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireAdmin?: boolean
  requireApprovedUser?: boolean
  redirectTo?: string
}

export function AuthGuard({ 
  children, 
  requireAuth = true, 
  requireAdmin = false, 
  requireApprovedUser = false,
  redirectTo = '/signin'
}: AuthGuardProps) {
  const { isAuthenticated, isAdmin, isApprovedUser, isLoading } = useAuth()
  const router = useRouter()

  useEffect(() => {
    if (isLoading) return

    if (requireAuth && !isAuthenticated) {
      router.replace(redirectTo)
      return
    }

    if (requireAdmin && !isAdmin) {
      router.replace('/signin?error=admin-required')
      return
    }

    if (requireApprovedUser && !isApprovedUser) {
      router.replace('/signin?error=approval-required')
      return
    }
  }, [isAuthenticated, isAdmin, isApprovedUser, isLoading, requireAuth, requireAdmin, requireApprovedUser, redirectTo, router])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (requireAuth && !isAuthenticated) {
    return null
  }

  if (requireAdmin && !isAdmin) {
    return null
  }

  if (requireApprovedUser && !isApprovedUser) {
    return null
  }

  return <>{children}</>
}
